import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  CoupleAuthData, 
  StoredCoupleAuth, 
  CreateCoupleRequest, 
  LoginCoupleRequest,
  PartnerRole 
} from '../../types/authentication';
import { 
  getStoredCoupleAuth, 
  isAuthenticationValid, 
  clearStoredAuth 
} from '../../lib/CoupleAuthentication';

// Async thunk for creating a couple account
export const createCoupleAccount = createAsyncThunk(
  'auth/createCouple',
  async (coupleData: CreateCoupleRequest) => {
    const response = await fetch('/.netlify/functions/createCouple', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(coupleData),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to create couple account');
    }
    
    return result.data;
  }
);

// Async thunk for couple login
export const loginCouple = createAsyncThunk(
  'auth/login',
  async (loginData: LoginCoupleRequest) => {
    const response = await fetch('/.netlify/functions/loginCouple', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginData),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to login');
    }
    
    return result.data;
  }
);

// Async thunk for checking stored authentication
export const checkStoredAuth = createAsyncThunk(
  'auth/checkStored',
  async () => {
    const storedAuth = getStoredCoupleAuth();
    
    if (!storedAuth || !storedAuth.is_valid) {
      throw new Error('No valid stored authentication found');
    }
    
    return storedAuth;
  }
);

interface AuthState {
  // Authentication status
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Current couple data
  coupleId: string | null;
  partner1Name: string | null;
  partner2Name: string | null;
  accessToken: string | null;
  expiresAt: string | null;
  
  // Current user's partner role
  currentPartnerRole: PartnerRole | null;
  
  // Error handling
  error: string | null;
  
  // UI states
  isCreatingAccount: boolean;
  isLoggingIn: boolean;
}

// Load initial state from storage
const loadInitialState = (): AuthState => {
  const storedAuth = getStoredCoupleAuth();
  
  if (storedAuth && storedAuth.is_valid) {
    return {
      isAuthenticated: true,
      isLoading: false,
      coupleId: storedAuth.id,
      partner1Name: storedAuth.partner1_name,
      partner2Name: storedAuth.partner2_name,
      accessToken: storedAuth.access_token,
      expiresAt: storedAuth.expires_at,
      currentPartnerRole: null, // Will be set on login
      error: null,
      isCreatingAccount: false,
      isLoggingIn: false,
    };
  }
  
  return {
    isAuthenticated: false,
    isLoading: false,
    coupleId: null,
    partner1Name: null,
    partner2Name: null,
    accessToken: null,
    expiresAt: null,
    currentPartnerRole: null,
    error: null,
    isCreatingAccount: false,
    isLoggingIn: false,
  };
};

const initialState: AuthState = loadInitialState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Set current partner role (after login)
    setCurrentPartnerRole: (state, action: PayloadAction<PartnerRole>) => {
      state.currentPartnerRole = action.payload;
    },
    
    // Logout
    logout: (state) => {
      clearStoredAuth();
      state.isAuthenticated = false;
      state.coupleId = null;
      state.partner1Name = null;
      state.partner2Name = null;
      state.accessToken = null;
      state.expiresAt = null;
      state.currentPartnerRole = null;
      state.error = null;
    },
    
    // Update authentication data (for token refresh)
    updateAuthData: (state, action: PayloadAction<{
      accessToken: string;
      expiresAt: string;
    }>) => {
      state.accessToken = action.payload.accessToken;
      state.expiresAt = action.payload.expiresAt;
    },
    
    // Update couple profile data
    updateCoupleProfileData: (state, action: PayloadAction<{
      partner1Name?: string;
      partner2Name?: string;
    }>) => {
      if (action.payload.partner1Name !== undefined) {
        state.partner1Name = action.payload.partner1Name;
      }
      if (action.payload.partner2Name !== undefined) {
        state.partner2Name = action.payload.partner2Name;
      }
    },
    
    // Reset loading states
    resetLoadingStates: (state) => {
      state.isLoading = false;
      state.isCreatingAccount = false;
      state.isLoggingIn = false;
    },
  },
  extraReducers: (builder) => {
    // Create couple account
    builder
      .addCase(createCoupleAccount.pending, (state) => {
        state.isCreatingAccount = true;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCoupleAccount.fulfilled, (state, action: PayloadAction<CoupleAuthData>) => {
        state.isCreatingAccount = false;
        state.isLoading = false;
        state.isAuthenticated = true;
        state.coupleId = action.payload.id;
        state.partner1Name = action.payload.partner1_name;
        state.partner2Name = action.payload.partner2_name;
        state.accessToken = action.payload.access_token;
        state.expiresAt = action.payload.expires_at;
        state.currentPartnerRole = 'partner1'; // Default to partner1 for account creator
        state.error = null;
      })
      .addCase(createCoupleAccount.rejected, (state, action) => {
        state.isCreatingAccount = false;
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create couple account';
      });
    
    // Login couple
    builder
      .addCase(loginCouple.pending, (state) => {
        state.isLoggingIn = true;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginCouple.fulfilled, (state, action: PayloadAction<CoupleAuthData>) => {
        state.isLoggingIn = false;
        state.isLoading = false;
        state.isAuthenticated = true;
        state.coupleId = action.payload.id;
        state.partner1Name = action.payload.partner1_name;
        state.partner2Name = action.payload.partner2_name;
        state.accessToken = action.payload.access_token;
        state.expiresAt = action.payload.expires_at;
        // currentPartnerRole will be set by the component based on login data
        state.error = null;
      })
      .addCase(loginCouple.rejected, (state, action) => {
        state.isLoggingIn = false;
        state.isLoading = false;
        state.error = action.error.message || 'Failed to login';
      });
    
    // Check stored authentication
    builder
      .addCase(checkStoredAuth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkStoredAuth.fulfilled, (state, action: PayloadAction<StoredCoupleAuth>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.coupleId = action.payload.id;
        state.partner1Name = action.payload.partner1_name;
        state.partner2Name = action.payload.partner2_name;
        state.accessToken = action.payload.access_token;
        state.expiresAt = action.payload.expires_at;
        state.error = null;
      })
      .addCase(checkStoredAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.coupleId = null;
        state.partner1Name = null;
        state.partner2Name = null;
        state.accessToken = null;
        state.expiresAt = null;
        state.currentPartnerRole = null;
        // Don't set error for failed stored auth check
      });
  },
});

export const {
  clearError,
  setCurrentPartnerRole,
  logout,
  updateAuthData,
  updateCoupleProfileData,
  resetLoadingStates,
} = authSlice.actions;

// Selectors
export const selectCoupleAuthData = (state: { auth: AuthState }) => {
  if (!state.auth.isAuthenticated || !state.auth.coupleId) {
    return null;
  }
  
  return {
    id: state.auth.coupleId,
    partner1_name: state.auth.partner1Name,
    partner2_name: state.auth.partner2Name,
    access_token: state.auth.accessToken,
    expires_at: state.auth.expiresAt,
  };
};

export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectCurrentPartnerRole = (state: { auth: AuthState }) => state.auth.currentPartnerRole;
export const selectPartnerNames = (state: { auth: AuthState }) => ({
  partner1Name: state.auth.partner1Name,
  partner2Name: state.auth.partner2Name,
});

export default authSlice.reducer;